import React, { useEffect, useState } from 'react';

import { cssMap } from '@compiled/react';
import type { IntlShape, MessageDescriptor } from 'react-intl';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import {
	EditorToolbarProvider,
	EditorToolbarUIProvider,
	TOOLBARS,
} from '@atlaskit/editor-common/toolbar';
import type {
	Command,
	CommandDispatch,
	ExtractInjectionAPI,
	FloatingToolbarButton,
	FloatingToolbarCustom,
	Icon,
} from '@atlaskit/editor-common/types';
import {
	FloatingToolbarButton as FloatingToolbarButtonComponent,
	FloatingToolbarSeparator as FloatingToolbarSeparatorComponent,
} from '@atlaskit/editor-common/ui';
import type { MarkType, NodeType } from '@atlaskit/editor-prosemirror/model';
import { TextSelection, type EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	ToolbarButtonGroup,
	ToolbarDropdownItem,
	ToolbarDropdownItemSection,
	ToolbarDropdownMenu,
	ToolbarDropdownMenuProvider,
	ToolbarSection,
} from '@atlaskit/editor-toolbar';
import { ToolbarModelRenderer } from '@atlaskit/editor-toolbar-model';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import { useSmartLinkContext } from '@atlaskit/link-provider';
import { RovoIcon } from '@atlaskit/logo';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { token } from '@atlaskit/tokens';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';

import type { AIPlugin } from '../../../aiPluginType';
import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn, EditorPluginAIProvider } from '../../../types/types';
import { SummarizeIcon } from '../../icons/prompt/summarize';
import type { EditorPluginAIConfigItem } from '../../prebuilt/config-items/config-items';
import { improveWriting } from '../../prebuilt/config-items/prompts/enhance/enhance';
import { summarizeSmartlink } from '../../prebuilt/config-items/prompts/summarize-smartlink/summarize-smartlink';
import { AIToolbarComponents } from '../toolbar';

import { getSmartLinkDropdownItems } from './smartlink-action-items';

const rovoButtonIconStyles = cssMap({
	root: {
		paddingInlineStart: token('space.075'),
		paddingInlineEnd: token('space.025'),
	},
	smallerRovoIcon: {
		display: 'inline-flex',
		transform: 'scale(0.75)',
		transformOrigin: 'center',
	},
});

export const floatingToolbarAIButtonTestID = `ak-editor-ai-selection-toolbar-ai-button`;

const ATLASSIAN_PRODUCTS = new Set([
	'jira',
	'confluence',
	'bitbucket',
	'trello',
	'loom',
	'townsquare',
	'compass',
	'mercury',
	'atlas',
	'watermelon',
	'canvas',
	'rovo',
]);

export const getAIExperienceButton = ({
	intl,
	rangeBaseGenerate,
	hideTitle,
	aiGlobalOptIn,
	icon,
	title,
	tooltip,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
	openInRovoChat,
	rovoChatAIFeatureContext,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
	hideTitle?: boolean;
	icon?: Icon;
	intl: IntlShape;
	onClick?: () => void;
	onMount?: () => void;
	openInRovoChat?: boolean;
	pulse?: boolean;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	rovoChatAIFeatureContext?: Record<string, unknown>;
	title?: MessageDescriptor;
	tooltip?: MessageDescriptor;
	triggeredFor?: NodeType;
}) => {
	let didMouseEnter = false;

	const browser = getBrowserInfo();
	const metaKey = browser.mac ? '⌘' : 'Ctrl';
	const Icon = () => <RovoIcon size="small"></RovoIcon>;
	return {
		type: 'button',
		isRadioButton: false,
		pulse,
		showTitle: true,
		testId: floatingToolbarAIButtonTestID,
		icon: icon ?? Icon,
		...(!hideTitle && {
			title: intl.formatMessage(title ?? aiMessages.aiToolbarTitle),
		}),
		ariaLabel: intl.formatMessage(title ?? aiMessages.aiToolbarTitle),
		tooltipContent: intl.formatMessage(tooltip ?? sharedMessages.aiUniversalShortCallToAction, {
			key: metaKey,
		}),
		onMount,
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			const configItem = openInRovoChat
				? { ...rangeBaseGenerate, openInRovoChat: true, rovoChatAIFeatureContext }
				: rangeBaseGenerate;

			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem,
				lastTriggeredFrom: 'floatingToolbar',
				// ED-18194 Added analytics for floating toolbar to determine
				// the % of users who click the button vs shift tab to it
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				triggeredFor,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			if (onClick) {
				onClick();
			}

			return false;
		},
		disabled,
	} as FloatingToolbarButton<Command>;
};

export const getImproveWritingAIButton = ({
	intl,
	hideTitle,
	aiGlobalOptIn,
	disabled,
	onMount,
	isInDocumentStreamingEnabled,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
	hideTitle?: boolean;
	intl: IntlShape;
	isInDocumentStreamingEnabled?: boolean;
	onMount?: () => void;
}) => {
	let didMouseEnter = false;

	return {
		type: 'button',
		isRadioButton: false,
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-improve-writing`,
		ariaLabel: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
		icon:
			fg('ai-smart-button-team-25') || editorExperiment('platform_editor_controls', 'variant1')
				? MagicWandIcon
				: undefined,
		...(!hideTitle && {
			title: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
		}),
		tooltipContent: isInDocumentStreamingEnabled
			? undefined
			: intl.formatMessage(aiMessages.improveWritingToolbarIconTooltip),
		onMouseEnter: () => {
			didMouseEnter = true;
			return false;
		},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: improveWriting(),
				lastTriggeredFrom: 'floatingToolbar',
				triggerMethod: !didMouseEnter ? 'keyboard' : 'click',
				aiGlobalOptIn,
				locale: intl.locale,
			});

			openAIModalCommand(state, dispatch);
			return false;
		},
		disabled,
		onMount,
	} as FloatingToolbarButton<Command>;
};

export const getAskAIButton = ({
	intl,
	rangeBaseGenerate,
	aiGlobalOptIn,
	triggeredFor,
	pulse,
	onMount,
	onClick,
	disabled,
	openInRovoChat,
	rovoChatAIFeatureContext,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	disabled: boolean;
	intl: IntlShape;
	onClick?: () => void;
	onMount?: () => void;
	openInRovoChat?: boolean;
	pulse?: boolean;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	rovoChatAIFeatureContext?: Record<string, unknown>;
	triggeredFor?: NodeType;
}): FloatingToolbarButton<Command> => {
	const Icon = () => <RovoIcon size="small"></RovoIcon>;
	return getAIExperienceButton({
		intl,
		rangeBaseGenerate,
		hideTitle: false,
		aiGlobalOptIn,
		icon: Icon,
		title: aiMessages.askRovoToolbarIconTitle,
		tooltip: aiMessages.askRovoToolbarIconTooltipQuickCommand,
		triggeredFor,
		pulse,
		onMount,
		onClick,
		disabled,
		openInRovoChat,
		rovoChatAIFeatureContext,
	});
};

// Renderer component to allow React hooks for memoization
const RovoSplitButtonRenderer = ({
	view,
	api,
	editorPluginAIProvider,
	aiGlobalOptIn,
	onMount,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	onMount?: () => void;
	view: EditorView;
}) => {
	const registeredComponents = api?.toolbar?.actions.getComponents?.();

	const components = React.useMemo(
		() =>
			registeredComponents ??
			AIToolbarComponents({
				editorPluginAIProvider,
				aiGlobalOptIn,
				isNewToolbarEnabled: false,
				api,
			}),
		[registeredComponents, editorPluginAIProvider, aiGlobalOptIn, api],
	);

	const toolbarConfig = React.useMemo(
		() => ({
			type: 'toolbar' as const,
			key: TOOLBARS.INLINE_TEXT_TOOLBAR,
			component: ({ children }: { children: React.ReactNode }) => <>{children}</>,
		}),
		[],
	);

	const fallbacks = React.useMemo(
		() => ({
			group: ToolbarButtonGroup,
			section: ToolbarSection,
			menuSection: ToolbarDropdownItemSection,
		}),
		[],
	);

	React.useEffect(() => {
		onMount?.();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<EditorToolbarProvider editorView={view ?? null}>
			<EditorToolbarUIProvider api={api}>
				<ToolbarModelRenderer
					toolbar={toolbarConfig}
					components={components}
					fallbacks={fallbacks}
				/>
			</EditorToolbarUIProvider>
		</EditorToolbarProvider>
	);
};

export const getAskRovoAiSplitButton = (props: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	disabled: boolean;
	editorPluginAIProvider: EditorPluginAIProvider;
	intl: IntlShape;
	onMount?: () => void;
	pulse?: boolean;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	triggeredFor?: NodeType;
}): FloatingToolbarCustom<Command> => {
	const {
		intl,
		rangeBaseGenerate,
		aiGlobalOptIn,
		editorPluginAIProvider,
		disabled,
		api,
		triggeredFor,
		pulse,
		onMount,
	} = props;

	return {
		type: 'custom',
		fallback: [
			getAskAIButton({
				intl,
				rangeBaseGenerate,
				aiGlobalOptIn: props.aiGlobalOptIn,
				triggeredFor,
				pulse,
				onMount,
				disabled,
			}),
		],
		render: (view) => {
			if (!view) {
				return null;
			}

			return (
				<RovoSplitButtonRenderer
					onMount={onMount}
					view={view}
					api={api}
					editorPluginAIProvider={editorPluginAIProvider}
					aiGlobalOptIn={aiGlobalOptIn}
				/>
			);
		},
	};
};

export const getSmartLinkRovoButton = ({
	api,
	aiGlobalOptIn,
	disabled,
	has3pSources,
	intl,
	nodeType,
	rangeBaseGenerate,
	smartlinkName,
	smartlinkUrl,
	sourceProduct,
	triggeredForMark,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	has3pSources?: boolean;
	intl: IntlShape;
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkName: string;
	smartlinkUrl: string;
	sourceProduct?: string | null;
	triggeredForMark?: MarkType;
}): FloatingToolbarButton<Command> => {
	const RovoButtonIcon = () => (
		<Box as="span" xcss={rovoButtonIconStyles.root}>
			<Box as="span" xcss={rovoButtonIconStyles.smallerRovoIcon}>
				<RovoIcon size="xxsmall" />
			</Box>
		</Box>
	);

	return {
		type: 'button',
		isRadioButton: false,
		disabled,
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-smartlink`,
		icon: RovoButtonIcon,
		title: intl.formatMessage(sharedMessages.rovoButtonTitle),
		ariaLabel: intl.formatMessage(sharedMessages.rovoButtonTitle),
		tooltipContent: intl.formatMessage(sharedMessages.rovoButtonTitle),
		onMount: () => {},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			api?.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				eventType: EVENT_TYPE.UI,
				actionSubjectId: ACTION_SUBJECT_ID.SMART_LINK_ROVO_BUTTON,
				attributes: {
					sourceProduct,
					has3pSources,
				},
			});
			// Open the same prompt input as getAIExperienceButton(), but with
			// openInRovoChat: true so that submitting the prompt opens the Rovo
			// chat sidebar instead of performing an AI edit on the document.
			// rovoChatAIFeatureContext carries the smartlink entity context so that
			// the Rovo sidebar shows a context banner for the linked resource.
			const openAIModalCommand = createOpenAIModalCommand({
				state,
				configItem: {
					...rangeBaseGenerate,
					openInRovoChat: true,
					rovoChatAIFeatureContext: smartlinkName
						? {
								projectContext: {
									projectId: smartlinkUrl,
									projectName: smartlinkName,
									projectUrl: smartlinkUrl,
								},
							}
						: undefined,
				},
				lastTriggeredFrom: 'floatingToolbar',
				triggerMethod: 'click',
				aiGlobalOptIn,
				triggeredFor: nodeType,
				triggeredForMark,
				locale: intl.locale,
			});
			openAIModalCommand(state, dispatch);
			return false;
		},
	};
};

export const getSmartLinkSummarizeButton = ({
	api,
	disabled,
	intl,
	aiGlobalOptIn,
	nodeType,
	nodeRange,
	smartlinkUrl,
	sourceProduct,
	has3pSources,
	triggeredForMark,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	has3pSources?: boolean;
	intl: IntlShape;
	/**
	 * Document range of the smartlink the toolbar was opened for. Preferred over
	 * `state.selection`, which is the *current* selection — and may not match the
	 * smartlink (e.g. when the toolbar is shown via hover/focus rather than a
	 * click that selects the node).
	 */
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	smartlinkUrl: string;
	sourceProduct?: string | null;
	triggeredForMark?: MarkType;
}): FloatingToolbarButton<Command> => {
	return {
		type: 'button',
		isRadioButton: false,
		disabled,
		showTitle: true,
		testId: `${floatingToolbarAIButtonTestID}-smartlink-summarize`,
		icon: SummarizeIcon,
		title: intl.formatMessage(aiMessages.summarizeToolbarDropdownIconTitle),
		ariaLabel: intl.formatMessage(aiMessages.summarizeToolbarDropdownIconTitle),
		tooltipContent: intl.formatMessage(aiMessages.summarizeToolbarDropdownIconTitle),
		onMount: () => {},
		onClick: (state: EditorState, dispatch?: CommandDispatch, _view?: EditorView) => {
			api?.analytics?.actions.fireAnalyticsEvent({
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				eventType: EVENT_TYPE.UI,
				actionSubjectId: ACTION_SUBJECT_ID.SMART_LINK_SUMMARIZE_BUTTON,
				attributes: {
					sourceProduct,
					has3pSources,
				},
			});

			if (api?.aiDefinitions?.actions.openSmartLinkSummarizePopup) {
				// Prefer the smartlink's own range (passed in from the descriptor
				// builder, where it's already known) over `state.selection` —
				// the toolbar can be shown via hover/focus without the selection
				// covering the node, in which case `state.selection` would point
				// at unrelated content.
				const { from, to } = nodeRange ?? state.selection;
				// Deselect the smartlink node before opening the popup so the
				// smartlink floating toolbar closes immediately. Clamp the target
				// position to the document end — `to + 1` would overshoot when a
				// blockCard sits at the very end of the doc.
				if (dispatch) {
					const deselectAt = Math.min(to + 1, state.doc.content.size);
					let tr = state.tr.setSelection(TextSelection.near(state.doc.resolve(deselectAt), 1));
					if (api.card?.actions.hideLinkToolbar) {
						tr = api.card.actions.hideLinkToolbar(tr);
					}
					dispatch(tr);
				}
				api.aiDefinitions.actions.openSmartLinkSummarizePopup({
					from,
					to,
					smartlinkUrl,
				});
				return false;
			} else {
				const openAIModalCommand = createOpenAIModalCommand({
					state,
					configItem: summarizeSmartlink({ smartlinkUrl }),
					lastTriggeredFrom: 'floatingToolbar',
					triggerMethod: 'click',
					aiGlobalOptIn,
					locale: intl.locale,
					triggeredFor: nodeType,
					sourceProduct,
					has3pSources,
					triggeredForMark,
				});
				openAIModalCommand(state, dispatch);
			}
			return false;
		},
	};
};

/**
 * Renders the SmartLink AI buttons (Rovo + Summarize) only when the SmartLink
 * for the given URL has been resolved.
 */
const SmartLinkAIButtonsRenderer = ({
	api,
	aiGlobalOptIn,
	areAnyNewToolbarFlagsEnabled,
	disabled,
	intl,
	isHyperlink,
	nodeType,
	nodeRange,
	rangeBaseGenerate,
	smartlinkUrl,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	intl: IntlShape;
	isHyperlink?: boolean;
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
	view: EditorView;
}): React.JSX.Element | null => {
	const { store, connections } = useSmartLinkContext();
	const [hyperlinkResolved, setHyperlinkResolved] = useState(false);

	// For plain hyperlinks, the URL is not yet in the SmartLink store (only card
	// components trigger resolution automatically). We trigger resolution on mount
	// so we can check access permissions before showing AI buttons.
	useEffect(() => {
		if (!isHyperlink) {
			return;
		}
		const existing = store?.getState()[smartlinkUrl];
		if (existing) {
			setHyperlinkResolved(true);
			return;
		}
		connections.client
			.fetchData(smartlinkUrl)
			.then((response) => {
				store?.dispatch({
					type: 'resolved',
					url: smartlinkUrl,
					payload: response,
				});
				setHyperlinkResolved(true);
			})
			.catch(() => {
				// If resolution fails, buttons won't render — correct behavior
				// for inaccessible URLs.
			});
	}, [isHyperlink, smartlinkUrl, store, connections.client]);

	const smartLinkState = store?.getState()[smartlinkUrl];
	const { status, details } = smartLinkState || {};
	const objectProviderKey = details?.meta?.key?.toLowerCase() ?? '';
	const hasObjectProviderKey = objectProviderKey.length > 0;
	const isThirdPartyNonPublic = !Array.from(ATLASSIAN_PRODUCTS).some((product) => objectProviderKey.includes(product)) && !objectProviderKey.includes('public');

	const has3pSources = hasObjectProviderKey ? isThirdPartyNonPublic: false;

	const sourceProduct = hasObjectProviderKey ? objectProviderKey.split('-')[0] : null;

	const isResolved = status === 'resolved' && details?.meta?.access === 'granted';

	// For hyperlinks, wait for resolution to complete before deciding
	if (isHyperlink && !hyperlinkResolved) {
		return null;
	}

	if (!isResolved) {
		return null;
	}

	const triggeredForMark = isHyperlink ? view.state.schema.marks.link : undefined;

	const getHostnameFromUrl = (url: string): string => {
		try {
			return new URL(url).hostname;
		} catch {
			return '';
		}
	};
	// Re-use the existing descriptor-based helpers to keep all button config in
	// one place, then pass each field to the FloatingToolbarButton React component
	// the same way the Toolbar's own `case 'button':` branch does it.
	// Extract the human-readable name from the SmartLink's JSON-LD data (the
	// `name` field on BaseData). Fall back to the URL hostname so the Rovo chat
	// context banner always has a non-empty label.
	const smartlinkData = details?.data as { name?: unknown } | undefined;
	const smartlinkName =
		typeof smartlinkData?.name === 'string' && smartlinkData.name
			? smartlinkData.name
			: getHostnameFromUrl(smartlinkUrl);

	const rovoDescriptor = getSmartLinkRovoButton({
		api,
		aiGlobalOptIn,
		disabled,
		has3pSources,
		intl,
		nodeType,
		rangeBaseGenerate,
		smartlinkName,
		smartlinkUrl,
		sourceProduct,
		triggeredForMark,
	});
	const summarizeDescriptor = getSmartLinkSummarizeButton({
		api,
		aiGlobalOptIn,
		disabled,
		intl,
		nodeType,
		nodeRange,
		smartlinkUrl,
		sourceProduct,
		has3pSources,
		triggeredForMark,
	});

	const dispatchCommand = (cmd: Command) => {
		cmd(view.state, view.dispatch, view);
	};

	const isSmartlinkToolbarV2ExpEnabled = expValEquals(
		'platform_editor_ai_smartlink_toolbar_v2_exp',
		'isEnabled',
		true,
	);

	const dropdownItems = isSmartlinkToolbarV2ExpEnabled
		? getSmartLinkDropdownItems({
				aiGlobalOptIn,
				details,
				intl,
				nodeType,
				smartlinkUrl,
				testIdPrefix: floatingToolbarAIButtonTestID,
				triggeredForMark,
				view,
			})
		: [];

	const RovoIcon = rovoDescriptor.icon;
	const SummarizeButtonIcon = summarizeDescriptor.icon;

	const shouldShowDropdown = isSmartlinkToolbarV2ExpEnabled && dropdownItems.length > 0;

	return (
		<>
			<FloatingToolbarButtonComponent
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				testId={rovoDescriptor.testId}
				title={rovoDescriptor.title}
				ariaLabel={rovoDescriptor.ariaLabel}
				tooltipContent={rovoDescriptor.tooltipContent}
				disabled={rovoDescriptor.disabled}
				icon={RovoIcon ? <RovoIcon label="" /> : undefined}
				onMount={rovoDescriptor.onMount}
				/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
				onClick={() => dispatchCommand(rovoDescriptor.onClick)}
			>
				{rovoDescriptor.showTitle && rovoDescriptor.title}
			</FloatingToolbarButtonComponent>
			{shouldShowDropdown && (
				<ToolbarDropdownMenuProvider>
					<ToolbarDropdownMenu
						iconBefore={<ChevronDownIcon label="" size="small" />}
						testId={`${floatingToolbarAIButtonTestID}-smartlink-rovo-chevron`}
						isDisabled={disabled}
					>
						<ToolbarDropdownItemSection>
							{dropdownItems.map((item) => (
								<ToolbarDropdownItem
									key={item.testId}
									elemBefore={item.icon}
									isDisabled={disabled}
									onClick={item.onClick}
									testId={item.testId}
								>
									{item.label}
								</ToolbarDropdownItem>
							))}
						</ToolbarDropdownItemSection>
					</ToolbarDropdownMenu>
				</ToolbarDropdownMenuProvider>
			)}
			<FloatingToolbarButtonComponent
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				testId={summarizeDescriptor.testId}
				title={summarizeDescriptor.title}
				ariaLabel={summarizeDescriptor.ariaLabel}
				tooltipContent={summarizeDescriptor.tooltipContent}
				disabled={summarizeDescriptor.disabled}
				icon={SummarizeButtonIcon ? <SummarizeButtonIcon label="" /> : undefined}
				onMount={summarizeDescriptor.onMount}
				/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
				onClick={() => dispatchCommand(summarizeDescriptor.onClick)}
			>
				{summarizeDescriptor.showTitle && summarizeDescriptor.title}
			</FloatingToolbarButtonComponent>
			<FloatingToolbarSeparatorComponent
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				fullHeight={editorExperiment('platform_editor_controls', 'variant1')}
			/>
		</>
	);
};

/**
 * Returns a FloatingToolbarCustom item that conditionally injects the SmartLink
 * Rovo button and Summarize button into the card floating toolbar — but only
 * when the SmartLink is in a 'resolved' state.
 *
 * The `type: 'custom'` item's `render` function runs inside React (called by
 * the Toolbar component in editor-plugin-floating-toolbar), so it is safe to
 * use React hooks inside SmartLinkAIButtonsRenderer. The resolved-state gate
 * therefore happens at React render time rather than at toolbar-config-build
 * time (which runs outside React in the FloatingToolbarHandler).
 *
 * `fallback: []` means nothing is shown if React hasn't mounted yet, matching
 * the pattern used by getAskRovoAiSplitButton in this file.
 */
export const getSmartLinkAIButtons = ({
	api,
	aiGlobalOptIn,
	areAnyNewToolbarFlagsEnabled,
	disabled,
	intl,
	isHyperlink,
	nodeType,
	nodeRange,
	rangeBaseGenerate,
	smartlinkUrl,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api?: ExtractInjectionAPI<AIPlugin>;
	areAnyNewToolbarFlagsEnabled: boolean;
	disabled: boolean;
	intl: IntlShape;
	isHyperlink?: boolean;
	/**
	 * Document range of the smartlink (card node or hyperlink mark) the toolbar
	 * is anchored to. When omitted, downstream actions fall back to the current
	 * `state.selection`, which is fragile (the toolbar can be shown via hover or
	 * focus without the selection covering the node).
	 */
	nodeRange?: { from: number; to: number };
	nodeType: NodeType;
	rangeBaseGenerate: EditorPluginAIConfigItem;
	smartlinkUrl: string;
}): FloatingToolbarCustom<Command> => ({
	type: 'custom',
	fallback: [],
	render: (view) => {
		if (!view) {
			return null;
		}
		return (
			<SmartLinkAIButtonsRenderer
				view={view}
				intl={intl}
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				isHyperlink={isHyperlink}
				smartlinkUrl={smartlinkUrl}
				nodeType={nodeType}
				nodeRange={nodeRange}
				rangeBaseGenerate={rangeBaseGenerate}
				areAnyNewToolbarFlagsEnabled={areAnyNewToolbarFlagsEnabled}
				disabled={disabled}
			/>
		);
	},
});
